import 'photoswipe/dist/photoswipe.css'
import { skipToken } from '@apollo/client/react'
import type { PreparedPhotoSwipeOptions } from 'photoswipe'
import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { GalleryProps } from 'react-photoswipe-gallery'
import { Gallery, Item } from 'react-photoswipe-gallery'
import titleImageFile from 'src/assets/watermark/pixai-title.png'
import type { User } from 'src/services/apollo/generated/graphql'
import { useGetUserProfiles } from 'src/services/apollo/profile'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import { canvasToBlob, isOffscreenCanvas } from 'src/utils/imageUtil'
import { getMediaUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import { downloadFile, fetchToBlobUrl, loadImage } from '../ArtworkDetail/utils'

export type SingleImageViewerProps = {
  /**
   * Gallery ID. If omitted, artworkId will be used.
   */
  id?: string
  artworkId?: string
  originalUrl?: string
  thumbnailUrl?: string
  width?: number
  height?: number

  imgClassName?: string
  /**
   * Whether show thumbnail when content is loading.
   * Will change how layout works for this component.
   * default to false to keep compatibility
   */
  showFallbackThumbnail?: boolean
  /**
   * This only take effect when showFallbackThumbnail is true
   * use imgClassName when showFallbackThumbnail is false
   */
  containerClassName?: string

  author?: User
  enableWatermark?: boolean
  scene?: string
  alt?: string
  /** Pinterest pin description for the image */
  pinDescription?: string
  pswpOptions?: Partial<PreparedPhotoSwipeOptions>

  // Download Options
  /**
   * Will remove the whole uiElements customization
   */
  disableDownload?: boolean
  /**
   * fileName with extension
   */
  fileName: string
  /**
   * Skip converting image to PNG for downloading
   *
   * Some old animatedDiff tasks doesn't have an mp4 video
   * Skip conversions for those
   */
  skipDownloadConversion?: boolean
  /**
   * Newer animatedDiff tasks have an mp4 video
   * Prefer providing mp4 for downloading
   */
  videoMediaId?: string

  /** For download event tracking */
  mediaId?: string

  /** <Item>'s cropped */
  cropped?: boolean

  /**
   * Treat this image as a high-priority LCP candidate. When true, the
   * `<img>` and its fallback thumbnail get `loading="eager"` +
   * `fetchpriority="high"` so the browser fetches them on the critical
   * path. Use this on artwork / model detail hero images, where the
   * SSR HTML already ships a `<img src>` but without these hints the
   * browser deprioritizes the request and the hero slot stays blank
   * for a long time after first paint.
   */
  priority?: boolean
}

const uiElements = ({
  userId,
  authorId,
  artworkId,
  mediaId,
  scene,
  fileOptions,
}: {
  userId?: string
  authorId?: string
  artworkId?: string
  mediaId?: string
  scene?: string
  fileOptions: {
    fileName: string
    skipConversionRef?: RefObject<boolean | null>
    videoUrlRef?: RefObject<string | null>
  }
}): GalleryProps['uiElements'] => [
  {
    name: 'download-button',
    ariaLabel: 'Download',
    order: 8,
    isButton: true,
    tagName: 'button',
    appendTo: 'bar',
    html: {
      isCustomSVG: true,
      inner:
        '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z" id="pswp__icn-download"/>',
      outlineID: 'pswp__icn-download',
    },
    onClick: (e, el, pswpInstance) => {
      const fileUrl =
        fileOptions?.videoUrlRef?.current ?? pswpInstance.currSlide?.data.src

      const skipConversion =
        !!fileOptions?.videoUrlRef?.current ||
        (fileOptions?.skipConversionRef?.current ?? undefined)

      if (fileUrl) {
        downloadFile(fileUrl, fileOptions.fileName, {
          skipConversion,
        })
      }
      track('image_dl', {
        artworkId,
        authorId,
        isMyMedia: userId ? userId === authorId : undefined,
        scene,
        mediaId,
      })
    },
  },
]

// type UserInfo = {
//   twitterId: string | null
//   instagramId: string | null
//   name?: string | null
//   avatarMedia?: Media
// }

const WATERMARK_HEIGHT = 0

function watermarkedImageDimensions({
  originalWidth,
  originalHeight,
}: {
  originalWidth: number
  originalHeight: number
}) {
  // rewrite from:
  // const canvasWidth = originalImage.width
  // const canvasHeight = originalImage.height
  // // scale factor for large image
  // const scaleFactor = originalImage.width / 512

  // const canvas = document.createElement('canvas')

  // canvas.width = canvasWidth * dpr
  // canvas.height = (canvasHeight + WATERMARK_HEIGHT * scaleFactor) * dpr // bottom padding

  // `window` isn't available during SSR. The dimensions this helper
  // produces are only used once a watermarked image has been rendered
  // into a canvas, which is a pure client-side workflow — falling back
  // to `1` on the server keeps Photoswipe's `<Item width/height>` in
  // sync with the non-watermarked dimensions until hydrate swaps in
  // the real `devicePixelRatio`.
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1
  const scaleFactor = originalWidth / 512
  const width = originalWidth * dpr
  const height = (originalHeight + WATERMARK_HEIGHT * scaleFactor) * dpr

  return {
    width,
    height,
  }
}

// smaller for landscape image
const getTitleImageDimensions = ({
  originalWidth,
  originalHeight,
}: {
  originalWidth: number
  originalHeight: number
}) => {
  if (originalWidth > originalHeight) {
    return {
      width: 70.5, // 47 / 14
      height: 21,
    }
  }
  return {
    width: 94,
    height: 28,
  }
}

/**
 * Attach watermark banner at the bottom of the image
 * @param originalImage
 * @returns
 */
export async function watermarkImage(
  originalImage: { src: string; height: number; width: number },
  // info: UserInfo,
): Promise<string | null> {
  const canvasWidth = originalImage.width
  const canvasHeight = originalImage.height
  // scale factor for large image

  const canvas = offscreenCanvasAvailable
    ? new OffscreenCanvas(canvasWidth, canvasHeight)
    : document.createElement('canvas')

  // Disable image + canvas scaling
  // const dpr = window.devicePixelRatio
  // const scaleFactor = originalImage.width / 512
  // const dimensions = watermarkedImageDimensions({
  //   originalWidth: originalImage.width,
  //   originalHeight: originalImage.height,
  // })

  // canvas.width = dimensions.width
  // canvas.height = dimensions.height

  const artworkImage = new Image()
  let artworkUrl = ''

  const titleImage = new Image()
  let titleImageUrl = ''

  try {
    artworkUrl = await fetchToBlobUrl(originalImage.src)
    await loadImage(artworkUrl, artworkImage)
  } catch (e) {}

  try {
    titleImageUrl = await fetchToBlobUrl(titleImageFile)
    await loadImage(titleImageUrl, titleImage)
  } catch (e) {}

  function startDrawing(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  ) {
    ctx.drawImage(artworkImage, 0, 0, canvasWidth, canvasHeight)
    // ctx.drawImage(artworkImage, 0, 0, canvasWidth * dpr, canvasHeight * dpr)
    // scaling for screen scale
    // ctx.scale(dpr, dpr)
    // scale for large image
    // ctx.scale(scaleFactor, scaleFactor)

    /**
     * draw title image
     */
    const titleImageDim = getTitleImageDimensions({
      originalWidth: originalImage.width,
      originalHeight: originalImage.height,
    })

    ctx.save()
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(
      titleImage,
      canvas.width - titleImageDim.width - 16,
      canvas.height - titleImageDim.height - 16,
      titleImageDim.width,
      titleImageDim.height,
    )
    ctx.restore()

    // Scale back to original size
    // ctx.scale(1 / scaleFactor, 1 / scaleFactor)
    // ctx.scale(1 / dpr, 1 / dpr)
  }

  // Branches are identical, just to make typescript happy
  if (isOffscreenCanvas(canvas)) {
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return null
    startDrawing(ctx)
  } else {
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return null
    startDrawing(ctx)
  }
  /**
   *
   */

  URL.revokeObjectURL(artworkUrl)
  URL.revokeObjectURL(titleImageUrl)

  return new Promise(resolve => {
    canvasToBlob(canvas).then((blob: Blob | null) => {
      if (blob) {
        resolve(URL.createObjectURL(blob))
      } else {
        resolve(null)
      }
    })
  })
}

const SingleImageViewer = (props: SingleImageViewerProps) => {
  const { me } = useMe()

  const [watermarkedImage, setWatermarkedImage] = useState<string | null>(null)

  const videoUrlRef = useRef<string>(null)
  const skipConversionRef = useRef<boolean | null>(
    props.skipDownloadConversion ?? null,
  )

  // Don't fetch userProfiles unless watermark enabled
  const { data: { profiles: userProfiles } = {} } = useGetUserProfiles(
    props.enableWatermark ? (props.author?.id ?? skipToken) : skipToken,
  )

  // const info: UserInfo = {
  //   twitterId: userProfiles.twitterId ?? null,
  //   instagramId: userProfiles.instagramId ?? null,
  //   name: props.author?.displayName || props.author?.username,
  //   avatarMedia: props.author?.avatarMedia,
  // }

  useEffect(() => {
    if (!props.enableWatermark) {
      return
    }
    if (!props.originalUrl || !props.height || !props.width) {
      return
    }
    // ensure userProfiles are already loaded
    if (userProfiles) {
      watermarkImage({
        src: props.originalUrl,
        height: props.height,
        width: props.width,
      }).then(u => {
        if (u) {
          setWatermarkedImage(u)
        }
      })
    }
    return () => {
      if (watermarkedImage) URL.revokeObjectURL(watermarkedImage)
    }
  }, [
    props.originalUrl,
    props.width,
    props.height,
    props.enableWatermark,
    !!userProfiles,
  ])

  const displayingWatermarkedImage = props.enableWatermark && watermarkedImage
  const originalUrl = displayingWatermarkedImage
    ? watermarkedImage
    : props.originalUrl

  const originalImgDimension = { width: props.width, height: props.height }
  const watermarkedImgDimension = (() => {
    if (!props.width || !props.height)
      return { width: props.width, height: props.height }
    const watermarkedDimension = watermarkedImageDimensions({
      originalHeight: props.height,
      originalWidth: props.width,
    })
    return watermarkedDimension
  })()
  // use different dimension after watermarked image loaded
  const galleryImgDimension = displayingWatermarkedImage
    ? watermarkedImgDimension
    : originalImgDimension

  const loadingWatermark = props.enableWatermark && !watermarkedImage

  // Set video url to ref
  // Otherwise pswp can't get updated video url
  useEffect(() => {
    if (props.videoMediaId) {
      videoUrlRef.current = getMediaUrl(props.videoMediaId, 'orig') ?? null
    }
  }, [props.videoMediaId])
  useEffect(() => {
    skipConversionRef.current = props.skipDownloadConversion ?? null
  }, [props.skipDownloadConversion])

  return (
    <Gallery
      id={props.id ?? props.artworkId}
      uiElements={
        props.disableDownload
          ? undefined
          : uiElements({
              userId: me?.id,
              authorId: props.author?.id,
              artworkId: props.artworkId,
              mediaId: props.mediaId,
              scene: props.scene,
              fileOptions: {
                fileName: props.fileName,
                skipConversionRef,
                videoUrlRef,
              },
            })
      }
      options={{
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2,
        maxZoomLevel: 'fill',
        ...props.pswpOptions,
      }}
    >
      <Item
        original={originalUrl}
        thumbnail={props.thumbnailUrl}
        {...galleryImgDimension}
        cropped={props.cropped}
      >
        {({ ref, open }) => {
          const img = (
            <img
              crossOrigin="anonymous"
              width={props.width}
              height={props.height}
              ref={ref}
              onClick={e => {
                if (loadingWatermark) return
                return open(e)
              }}
              src={originalUrl}
              data-pin-description={
                props.pinDescription || props.alt || undefined
              }
              className={cx(
                'object-contain',
                loadingWatermark && 'cursor-wait',
                props.imgClassName,
              )}
              id={
                props.enableWatermark && watermarkedImage
                  ? 'watermarked-img-id'
                  : undefined
              }
              alt={props.alt}
              loading={props.priority ? 'eager' : undefined}
              fetchPriority={props.priority ? 'high' : undefined}
            />
          )
          return props.showFallbackThumbnail ? (
            <div className={cx('isolate relative', props.containerClassName)}>
              {img}
              <div className="-z-10 left-0 top-0 w-full h-full absolute">
                <img
                  className={cx('object-contain', props.imgClassName)}
                  src={props.thumbnailUrl}
                  loading={props.priority ? 'eager' : undefined}
                  fetchPriority={props.priority ? 'high' : undefined}
                />
              </div>
            </div>
          ) : (
            img
          )
        }}
      </Item>
    </Gallery>
  )
}

const offscreenCanvasAvailable =
  typeof window !== 'undefined' && typeof window.OffscreenCanvas === 'function'

export default SingleImageViewer
